import { css } from "lit";

/**
 * Shared styling for the focused single-task Add flows (see
 * docs/add-task-flows.md). Mirrors the established quick-gl-page look so every
 * flow feels like one family: light backdrop, centred narrow column, a white
 * card, large touch-friendly inputs, and a full-width save button.
 *
 * Compose with a page's own styles: `static styles = [flowStyles, css`...`]`.
 */
export const flowStyles = css`
  :host {
    display: block;
    min-height: 100vh;
    background: #f8f9fa;
  }

  .page-container {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    max-width: 440px;
    margin: 0 auto;
    padding: 1rem;
    box-sizing: border-box;
  }

  .page-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
  }

  .page-header h1 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: #1a1a1a;
  }

  .back-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: white;
    border: 1px solid #dee2e6;
    cursor: pointer;
    color: #495057;
    flex-shrink: 0;
  }

  .back-btn:hover {
    background: #e9ecef;
  }

  .header-badge {
    margin-left: auto;
    padding: 0.25rem 0.5rem;
    background: #e7f1ff;
    color: #0d6efd;
    border-radius: 0.375rem;
    font-size: 0.75rem;
    font-weight: 600;
  }

  .form-section {
    background: white;
    border-radius: 1rem;
    padding: 1.5rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .input-group {
    margin-bottom: 1rem;
  }

  .input-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #495057;
    margin-bottom: 0.5rem;
  }

  .input-label wa-icon {
    font-size: 1rem;
  }

  /* Large input styling for easy phone use */
  wa-input.flow-input::part(base) {
    border-radius: 0.75rem;
    border: 2px solid #dee2e6;
    font-size: 1.5rem;
    height: 60px;
  }

  wa-input.flow-input::part(input) {
    font-size: 1.5rem;
    text-align: center;
    font-weight: 600;
  }

  wa-input.flow-input::part(base):focus-within {
    border-color: #0d6efd;
    box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.25);
  }

  .unit-hint {
    font-size: 0.75rem;
    color: #6c757d;
    text-align: center;
    margin-top: 0.25rem;
  }

  .help-text {
    font-size: 0.875rem;
    color: #6c757d;
    margin-top: 1rem;
  }

  .actions {
    margin-top: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .save-btn::part(base) {
    height: 56px;
    font-size: 1.125rem;
    font-weight: 600;
    border-radius: 0.75rem;
  }

  .success-banner {
    position: fixed;
    top: 1rem;
    left: 50%;
    transform: translateX(-50%);
    z-index: 1000;
  }

  .warning-section {
    margin-top: 1rem;
  }

  .warning-section wa-callout {
    margin-bottom: 0.75rem;
  }

  /* Choice chips - reused by exercise type/intensity, sick status, P/F percent */
  .chip-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .chip {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.625rem 0.875rem;
    border-radius: 0.625rem;
    border: 2px solid #dee2e6;
    background: white;
    color: #495057;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
  }

  .chip:hover {
    background: #f1f3f5;
  }

  .chip.selected {
    border-color: #0d6efd;
    background: #e7f1ff;
    color: #0d6efd;
  }

  .chip wa-icon {
    font-size: 1.05rem;
  }

  .period-note {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.8rem;
    color: #6c757d;
    margin-top: 0.75rem;
  }

  .period-note button {
    background: none;
    border: none;
    color: #0d6efd;
    font: inherit;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
  }
`;
